import {
	coverageOf,
	DIGITS_SAMPLE,
	FONT_FIT_REFERENCE,
	type FontCoverage,
	type FontScript,
	fontString,
	PROBE_SIZE,
	primaryScript,
	quoteFamily,
	SCRIPT_GLYPHS,
	textMetrics,
} from "./probe";
import { setRootVar } from "./root";
import { FONT_LOAD_TIMEOUT_MS, withTimeout } from "./timing";

export { FONT_FIT_REFERENCE };

export type FontProfile = {
	widthCyr: number;
	widthLat: number;
	widthDigits: number;
	xHeight: number;
	capHeight: number;
	ascent: number;
	descent: number;
	lineBox: number;
};

export type FontFitAxis = "x" | "cap" | "line" | "width";

export type FontFitRatios = Record<FontFitAxis, number>;

export type FontDeviation = { metric: keyof FontProfile; ratio: number; percent: number };

export type FontFitVerdict = "same" | "fixable" | "breaks";

export type FontFitReport = {
	candidate: string;
	reference: string;
	verdict: FontFitVerdict;
	worst: number;
	deviations: FontDeviation[];
	ratios: FontFitRatios;
	reference_profile: FontProfile;
	candidate_profile: FontProfile;
};

export const FONT_FIT_VAR_PREFIX = "fontFit";
export const NO_FIT: FontFitRatios = { x: 1, cap: 1, line: 1, width: 1 };

const FONT_FIT_AXES: FontFitAxis[] = ["x", "cap", "line", "width"];
const SAME_THRESHOLD = 3;
const FIXABLE_THRESHOLD = 10;
const MIN_RATIO = 0.6;
const MAX_RATIO = 1.6;

const round = (value: number) => Math.round(value * 10000) / 10000;

const toPercent = (ratio: number) => Math.round((ratio - 1) * 1000) / 10;

const isUsableRatio = (value: number) => Number.isFinite(value) && value > 0;

const clampRatio = (value: number) => (isUsableRatio(value) ? round(Math.min(MAX_RATIO, Math.max(MIN_RATIO, value))) : 1);

const verdictOf = (worst: number): FontFitVerdict => {
	if (worst <= SAME_THRESHOLD) return "same";
	return worst <= FIXABLE_THRESHOLD ? "fixable" : "breaks";
};

export const isFontLoaded = (family: string) => document.fonts.check(`${PROBE_SIZE}px ${quoteFamily(family)}`);

const averageWidth = (font: string, text: string) => (textMetrics(font, text)?.width ?? 0) / text.length;

export const measureFont = (family: string, script: FontScript = "cyrillic"): FontProfile => {
	const font = `${PROBE_SIZE}px ${quoteFamily(family)}`;
	const glyphs = SCRIPT_GLYPHS[script];
	const x = textMetrics(font, glyphs.x);
	const cap = textMetrics(font, glyphs.cap);
	const ascent = cap?.fontBoundingBoxAscent ?? 0;
	const descent = cap?.fontBoundingBoxDescent ?? 0;
	return {
		widthCyr: averageWidth(font, SCRIPT_GLYPHS.cyrillic.sample),
		widthLat: averageWidth(font, SCRIPT_GLYPHS.latin.sample),
		widthDigits: averageWidth(font, DIGITS_SAMPLE),
		xHeight: x?.actualBoundingBoxAscent ?? 0,
		capHeight: cap?.actualBoundingBoxAscent ?? 0,
		ascent,
		descent,
		lineBox: ascent + descent,
	};
};

const comparableMetrics = (coverage: FontCoverage, script: FontScript | null): (keyof FontProfile)[] => [
	...(coverage.cyrillic ? (["widthCyr"] as const) : []),
	...(coverage.latin ? (["widthLat"] as const) : []),
	...(coverage.digits ? (["widthDigits"] as const) : []),
	...(script ? (["xHeight", "capHeight"] as const) : []),
	"ascent",
	"descent",
	"lineBox",
];

const deviationsOf = (metrics: (keyof FontProfile)[], candidate: FontProfile, reference: FontProfile): FontDeviation[] =>
	metrics.flatMap((metric) => {
		const ratio = candidate[metric] / reference[metric];
		return isUsableRatio(ratio) ? [{ metric, ratio, percent: toPercent(ratio) }] : [];
	});

const ratiosOf = (script: FontScript | null, candidate: FontProfile, reference: FontProfile): FontFitRatios => {
	if (!script) return NO_FIT;
	const width = script === "cyrillic" ? "widthCyr" : "widthLat";
	return {
		x: clampRatio(reference.xHeight / candidate.xHeight),
		cap: clampRatio(reference.capHeight / candidate.capHeight),
		line: clampRatio(reference.lineBox / candidate.lineBox),
		width: clampRatio(reference[width] / candidate[width]),
	};
};

export const compareFonts = (candidate: string, reference = FONT_FIT_REFERENCE): FontFitReport => {
	const coverage = coverageOf(candidate);
	const script = primaryScript(coverage);
	const referenceProfile = measureFont(reference, script ?? "cyrillic");
	const candidateProfile = measureFont(candidate, script ?? "cyrillic");
	const deviations = deviationsOf(comparableMetrics(coverage, script), candidateProfile, referenceProfile);
	const worst = deviations.length ? Math.max(...deviations.map((deviation) => Math.abs(deviation.percent))) : 0;
	return {
		candidate,
		reference,
		verdict: verdictOf(worst),
		worst,
		deviations,
		ratios: ratiosOf(script, candidateProfile, referenceProfile),
		reference_profile: referenceProfile,
		candidate_profile: candidateProfile,
	};
};

export const scaleRatios = (ratios: FontFitRatios, scale: number): FontFitRatios => ({
	x: round(ratios.x * scale),
	cap: round(ratios.cap * scale),
	line: round(ratios.line * scale),
	width: round(ratios.width * scale),
});

export const applyFontFit = (ratios: FontFitRatios, prefix = FONT_FIT_VAR_PREFIX) => {
	for (const axis of FONT_FIT_AXES) setRootVar(`${prefix}-${axis}`, String(isUsableRatio(ratios[axis]) ? ratios[axis] : 1));
};

export const resetFontFit = (prefix = FONT_FIT_VAR_PREFIX) => {
	for (const axis of FONT_FIT_AXES) setRootVar(`${prefix}-${axis}`, null);
};

export const measureFit = async (candidate: string, reference = FONT_FIT_REFERENCE): Promise<FontFitReport | null> => {
	if (candidate === reference) return null;
	try {
		await withTimeout(Promise.all([document.fonts.load(fontString(candidate)), document.fonts.load(fontString(reference))]), FONT_LOAD_TIMEOUT_MS);
	} catch {
		return null;
	}
	return isFontLoaded(candidate) && isFontLoaded(reference) ? compareFonts(candidate, reference) : null;
};

export const fitFont = async (candidate: string, reference = FONT_FIT_REFERENCE, prefix = FONT_FIT_VAR_PREFIX): Promise<FontFitReport | null> => {
	const report = await measureFit(candidate, reference);
	if (report) applyFontFit(report.ratios, prefix);
	else resetFontFit(prefix);
	return report;
};
